import { useState, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { useDayToday } from "@/lib/hooks";
import { fmtDuration, minutesToHours, hoursToMinutes } from "@/lib/utils";
import { DayBucketSelect, bucketValue, applyBucket } from "@/components/DayBucketSelect";
import { DayCheckoutModal } from "@/components/DayCheckoutModal";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sunrise, Plus, Trash2, Clock3, CheckCircle2, RotateCcw, Coffee, Sparkles } from "lucide-react";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";

const SKIP_KEY = (date) => `ias_daylog_skip_${date}`;
const BLUR_OVERLAY = "bg-foreground/25 backdrop-blur-md";

const LINE_PALETTE = ["#6B4F91", "#C967A3", "#5B8DEF", "#4C9A8E", "#E5A94C", "#8E9297", "#806DAA"];
const QUICK_PICKS = [
  { h: 0.25, label: "15m" },
  { h: 0.5, label: "30m" },
  { h: 1, label: "1h" },
  { h: 2, label: "2h" },
  { h: 4, label: "4h" },
];

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 20) return "Boa tarde";
  return "Boa noite";
}

const blankLine = () => ({
  id: crypto.randomUUID(),
  taskId: "",
  title: "",
  clientId: "",
  projectId: "",
  internal: true,
  plannedHours: 0.5,
});

function toFormLine(line) {
  return {
    id: line.id || crypto.randomUUID(),
    taskId: line.taskId || "",
    title: line.title || "",
    clientId: line.clientId || "",
    projectId: line.projectId || "",
    internal: Boolean(line.internal || (!line.clientId && !line.projectId)),
    plannedHours: minutesToHours(line.plannedMinutes),
  };
}

function toApiLine(line) {
  return {
    id: line.id,
    taskId: line.taskId || "",
    title: line.title.trim(),
    clientId: line.internal ? "" : line.clientId || "",
    projectId: line.internal ? "" : line.projectId || "",
    internal: line.internal,
    plannedMinutes: hoursToMinutes(line.plannedHours),
    actualMinutes: null,
    done: false,
    carriedToDate: "",
    deleteTask: false,
  };
}

export function DayCheckInCard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useDayToday();
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState(null);

  const date = data?.date;
  const dayLog = data?.dayLog;
  const needsPlanning = Boolean(data && !dayLog && !data.absence);

  useEffect(() => {
    if (!date) return;
    setSkipped(localStorage.getItem(SKIP_KEY(date)) === "1");
  }, [date]);

  useEffect(() => {
    if (data && !data.dayLog) {
      setLines((data.suggestedLines || []).map(toFormLine));
    }
  }, [data]);

  const totalPlanned = useMemo(
    () => lines.reduce((sum, l) => sum + hoursToMinutes(l.plannedHours), 0),
    [lines],
  );

  if (isLoading || !data) return null;

  const refetch = () => qc.invalidateQueries({ queryKey: ["day-log"] });
  const setLine = (id, patch) => setLines((ls) => ls.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  const removeLine = (id) => setLines((ls) => ls.filter((l) => l.id !== id));

  const skipToday = () => {
    localStorage.setItem(SKIP_KEY(date), "1");
    setSkipped(true);
  };
  const unskip = () => {
    localStorage.removeItem(SKIP_KEY(date));
    setSkipped(false);
  };

  const startDay = async () => {
    const clean = lines.filter((l) => l.title.trim());
    if (clean.length === 0) return toast.error("Adiciona pelo menos uma tarefa ao plano");
    setSaving(true);
    try {
      await api.post("/day-logs", { date, lines: clean.map(toApiLine), notes: "" });
      toast.success("Bom trabalho! Dia planeado.");
      refetch();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/day-logs/${dayLog.id}/reopen`);
      refetch();
      qc.invalidateQueries({ queryKey: ["time-entries"] });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const checkoutModal = (
    <DayCheckoutModal
      open={Boolean(checkoutFor)}
      onOpenChange={(v) => !v && setCheckoutFor(null)}
      dayLog={checkoutFor}
      onDone={() => {
        setCheckoutFor(null);
        refetch();
        qc.invalidateQueries({ queryKey: ["time-entries"] });
        qc.invalidateQueries({ queryKey: ["tasks"] });
      }}
    />
  );

  const pendingBanner = data.pendingClose && (
    <Card className="p-3 mb-4 border-amber-400/50 bg-amber-50/60 dark:bg-amber-950/20 flex items-center justify-between gap-3">
      <p className="text-sm font-roboto">
        Tens o dia <strong>{format(parseISO(data.pendingClose.date), "d 'de' MMMM", { locale: pt })}</strong> por fechar.
      </p>
      <Button size="sm" variant="outline" onClick={() => setCheckoutFor(data.pendingClose)}>Fechar agora</Button>
    </Card>
  );

  // ---- Ausente (férias / indisponível) ----
  if (data.absence) {
    return (
      <>
        {pendingBanner}
        <Card className="p-4 mb-6 border-border bg-muted/30 flex items-center gap-3">
          <Coffee className="h-5 w-5 text-primary" />
          <p className="text-sm font-roboto">Hoje estás marcado como ausente. Bom descanso! 🌴</p>
        </Card>
        {checkoutModal}
      </>
    );
  }

  // ---- Dia fechado ----
  if (dayLog?.status === "FECHADO") {
    const planned = (dayLog.lines || []).reduce((s, l) => s + (l.plannedMinutes || 0), 0);
    const actual = (dayLog.lines || []).reduce((s, l) => s + (l.actualMinutes || 0), 0);
    return (
      <>
        {pendingBanner}
        <Card className="p-4 mb-6 border-border bg-gradient-to-br from-primary/10 to-transparent">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-primary" />
              <h3 className="font-poppins font-semibold text-sm">Dia fechado</h3>
              <span className="text-xs text-muted-foreground">
                Real {fmtDuration(actual)} · Planeado {fmtDuration(planned)}
              </span>
            </div>
            <Button size="sm" variant="ghost" onClick={reopen}><RotateCcw className="h-4 w-4 mr-1" /> Reabrir</Button>
          </div>
        </Card>
        {checkoutModal}
      </>
    );
  }

  // ---- Dia em curso (planeado) ----
  if (dayLog?.status === "PLANEADO") {
    const planned = (dayLog.lines || []).reduce((s, l) => s + (l.plannedMinutes || 0), 0);
    return (
      <>
        {pendingBanner}
        <Card className="p-4 mb-6 border-border bg-gradient-to-br from-primary/10 to-transparent">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Clock3 className="h-5 w-5 text-primary" />
              <h3 className="font-poppins font-semibold text-sm">O plano de hoje</h3>
              <span className="text-xs text-muted-foreground">{fmtDuration(planned)} estimadas</span>
            </div>
            <Button size="sm" className="bg-primary text-black hover:bg-primary/90" onClick={() => setCheckoutFor(dayLog)}>
              Fechar o dia
            </Button>
          </div>
          <div className="space-y-1.5">
            {(dayLog.lines || []).map((l) => (
              <div key={l.id} className="flex items-center gap-2 text-sm">
                <span className="font-roboto flex-1 truncate">{l.title}</span>
                <span className="text-xs text-muted-foreground">{fmtDuration(l.plannedMinutes)}</span>
              </div>
            ))}
          </div>
        </Card>
        {checkoutModal}
      </>
    );
  }

  // ---- Por planear: dispensado por hoje ----
  if (skipped) {
    return (
      <>
        {pendingBanner}
        <Card className="p-3 mb-6 border-border bg-muted/20 flex items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground font-roboto">O plano de hoje ficou por fazer.</p>
          <Button size="sm" variant="outline" onClick={unskip}>Planear agora</Button>
        </Card>
        {checkoutModal}
      </>
    );
  }

  // ---- Por planear: pop-up de check-in ----
  const firstName = (user?.name || "").split(" ")[0];
  const filledLines = lines.filter((l) => hoursToMinutes(l.plannedHours) > 0);

  return (
    <>
      {pendingBanner}
      {checkoutModal}
      <Dialog open={needsPlanning && !skipped} onOpenChange={(v) => { if (!v) skipToday(); }}>
        <DialogContent
          overlayClassName={BLUR_OVERLAY}
          className="max-w-3xl w-[calc(100vw-2rem)] max-h-[92vh] overflow-hidden p-0 gap-0 rounded-2xl border-border/60 shadow-2xl"
          data-testid="day-checkin-modal"
        >
          {/* Cabeçalho */}
          <div className="relative overflow-hidden bg-gradient-to-br from-primary/30 via-accent to-card px-7 pt-7 pb-6">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/20 blur-2xl" aria-hidden />
            <div className="relative flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/20 ring-1 ring-primary/30">
                <Sunrise className="h-6 w-6 text-primary" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="font-poppins text-xl font-semibold tracking-tight">
                  {greeting()}{firstName ? `, ${firstName}` : ""} 👋
                </DialogTitle>
                <p className="mt-0.5 text-sm text-muted-foreground first-letter:uppercase">
                  {format(parseISO(date), "EEEE, d 'de' MMMM", { locale: pt })}
                </p>
                <p className="mt-2 max-w-md text-[13px] leading-relaxed text-muted-foreground font-roboto">
                  Estima quanto tempo achas que vais dedicar a cada tarefa. Nada fica gravado a ferro, ao
                  fim do dia corriges tudo.
                </p>
              </div>
            </div>
          </div>

          {/* Corpo */}
          <div className="max-h-[46vh] overflow-y-auto px-7 py-5">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground font-poppins">
              O que vais fazer hoje
            </p>

            {lines.length === 0 ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border py-8 text-center">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                  <Sparkles className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground font-roboto">
                  Sem tarefas atribuídas para hoje.<br />Adiciona o que tencionas fazer.
                </p>
              </div>
            ) : (
              <div className="space-y-2.5">
                {lines.map((line, i) => {
                  const color = LINE_PALETTE[i % LINE_PALETTE.length];
                  const activeH = Number(line.plannedHours);
                  return (
                    <div
                      key={line.id}
                      className="group rounded-xl border border-border bg-card p-3 transition-colors hover:border-primary/40"
                    >
                      <div className="flex items-center gap-2.5">
                        <span
                          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[11px] font-bold text-white"
                          style={{ backgroundColor: color }}
                        >
                          {i + 1}
                        </span>
                        <input
                          value={line.title}
                          onChange={(e) => setLine(line.id, { title: e.target.value })}
                          placeholder="O que vais fazer?"
                          disabled={Boolean(line.taskId)}
                          className="flex-1 bg-transparent text-[15px] font-medium outline-none placeholder:font-normal placeholder:text-muted-foreground disabled:opacity-70"
                        />
                        <button
                          onClick={() => removeLine(line.id)}
                          className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                          aria-label="Remover"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="mt-2.5 flex flex-wrap items-center gap-2 pl-[34px]">
                        <DayBucketSelect
                          value={bucketValue(line)}
                          onChange={(v) => setLine(line.id, applyBucket(v))}
                          className="h-8 w-[210px] text-xs"
                        />
                        <span className="mx-0.5 h-4 w-px bg-border" aria-hidden />
                        <div className="flex items-center gap-1">
                          {QUICK_PICKS.map((pick) => (
                            <button
                              key={pick.label}
                              type="button"
                              onClick={() => setLine(line.id, { plannedHours: pick.h })}
                              className={`rounded-md px-2 py-1 text-[11px] font-medium transition-colors ${
                                activeH === pick.h
                                  ? "bg-primary text-primary-foreground"
                                  : "bg-muted text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                              }`}
                            >
                              {pick.label}
                            </button>
                          ))}
                          <Input
                            type="number"
                            min="0"
                            step="0.25"
                            value={line.plannedHours}
                            onChange={(e) => setLine(line.id, { plannedHours: e.target.value })}
                            className="ml-1 h-8 w-14 text-sm"
                            aria-label="Horas"
                          />
                          <span className="text-xs text-muted-foreground">h</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <button
              onClick={() => setLines((ls) => [...ls, blankLine()])}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-border py-2.5 text-[13px] font-medium text-primary transition-colors hover:border-primary/50 hover:bg-primary/5"
            >
              <Plus className="h-4 w-4" /> Adicionar tarefa
            </button>
          </div>

          {/* Rodapé */}
          <div className="flex flex-wrap items-center gap-4 border-t border-border bg-muted/40 px-7 py-4">
            <div className="mr-auto">
              <div className="flex items-baseline gap-2">
                <span className="font-poppins text-lg font-semibold">{fmtDuration(totalPlanned)}</span>
                <span className="text-xs text-muted-foreground">
                  {filledLines.length} {filledLines.length === 1 ? "tarefa" : "tarefas"}
                </span>
              </div>
              {filledLines.length > 0 && (
                <div className="mt-1.5 flex h-2 w-44 overflow-hidden rounded-full bg-muted">
                  {filledLines.map((l) => (
                    <div
                      key={l.id}
                      style={{
                        width: `${(hoursToMinutes(l.plannedHours) / totalPlanned) * 100}%`,
                        backgroundColor: LINE_PALETTE[lines.indexOf(l) % LINE_PALETTE.length],
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
            <Button variant="ghost" onClick={skipToday}>Agora não</Button>
            <Button
              className="h-10 gap-1.5 bg-primary px-5 text-primary-foreground hover:bg-primary/90"
              onClick={startDay}
              disabled={saving}
              data-testid="day-start-btn"
            >
              <Sunrise className="h-4 w-4" />
              {saving ? "A guardar..." : "Começar o dia"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
